/**
 * Formatting for `StatusEffect.mechanical_effects`.
 *
 * Two halves, and they answer to different owners. The **notation** is read off the key —
 * anything ending `_multiplier` is arithmetic on a scale (`×0`), everything else is a
 * modifier (`−1`) — and stays in code, because `×` and U+2212 mean the same thing in every
 * locale we serve (L9.2). The **label** is a word a player reads, so it comes out of the
 * bundle: `ui.effects.field.<key>`, one entry per modifier the wire can carry.
 *
 * The set is closed, and more tightly than it looks. `MECHANICAL_EFFECT_KEYS`
 * (`quest/llm/tools.py`) is a seven-member tuple that `add_status_effect`'s schema declares
 * with `additionalProperties: false`, so strict mode stops the DM inventing an eighth name,
 * and `rules.derive_status_effects` sets those same seven and nothing else. The humanized
 * split survives anyway as the arm for a key with no label — that is a backend that grew a
 * modifier without a translation, i.e. a bug showing, not a supported render.
 */
import type { UiKey } from '@/i18n/en.generated';
import type { Translate } from '@/i18n/t';

/** `skill_penalty: -1` → `skill −1`; `healing_multiplier: 0` → `healing ×0`. */
export function formatMechanicalEffect(t: Translate, key: string, value: number): string {
  const multiplier = key.endsWith('_multiplier');
  const labelKey = FIELD_LABEL_KEYS[key];
  const label = labelKey === undefined ? humanize(key) : t(labelKey);
  // Numbers are printed raw, as every other number in this client is — `Intl.NumberFormat`
  // appears nowhere, and localizing the decimal separator on this one line would set `×1,2`
  // beside the vitals' `1.2` two rows up. That is a decision for all of them at once.
  return multiplier ? `${label} ×${String(value)}` : `${label} ${signed(value)}`;
}

/**
 * The seven modifiers the backend can send. Keyed by the **wire key**, not by the stem the
 * notation is read off, so `skill_penalty` and a future `skill_bonus` can be different words
 * in a language that needs them to be — and so this list can be diffed against
 * `MECHANICAL_EFFECT_KEYS` by eye. `test_i18n.py` does it by test.
 *
 * The English is deliberately the same wording the humanized split produced before these
 * labels existed, so `en` renders byte-identically; the two flag-ish keys read awkwardly in
 * it because the wire carries a boolean as a float, which is a schema question and not this
 * file's to fix.
 */
const FIELD_LABEL_KEYS: Record<string, UiKey> = {
  can_perform_physical: 'ui.effects.field.can_perform_physical',
  failure_chance_increase: 'ui.effects.field.failure_chance_increase',
  healing_multiplier: 'ui.effects.field.healing_multiplier',
  health_damage_per_hour: 'ui.effects.field.health_damage_per_hour',
  skill_penalty: 'ui.effects.field.skill_penalty',
  stamina_cost_multiplier: 'ui.effects.field.stamina_cost_multiplier',
  stamina_recovery_multiplier: 'ui.effects.field.stamina_recovery_multiplier',
};

function humanize(key: string): string {
  // The suffix is dropped once it has been used to pick the notation — "healing multiplier ×0"
  // says multiplier twice.
  return key.replace(/_(multiplier|penalty|bonus)$/, '').replaceAll('_', ' ');
}

function signed(value: number): string {
  // U+2212, so a modifier and the ticker's `−12 health` are set in the same glyph.
  return value < 0 ? `−${String(Math.abs(value))}` : `+${String(value)}`;
}
